import { GateTypes, Registration, RegistrationTypes, RequirementMap } from 'types/working-model';

/**
 * brandlive-platform adds two fields events-3's `Registration` type doesn't declare, but that are present
 * at runtime on events round-tripped from that app:
 *  - `project_gate_type`: the single active gate (or `null` for "open"). The new app keeps EVERY configured
 *    gate in `requirements` and only collapses to this one at publish.
 *  - `domain_gate_mode`: the `domain_email` sub-mode ('allow' vs 'block').
 */
export type RegistrationWithGatePointer = Registration & {
	project_gate_type?: GateTypes | null;
	domain_gate_mode?: 'allow' | 'block';
};

/**
 * Collapses `registration_settings` down to the single gate named by `project_gate_type`, and DROPS the
 * brandlive-platform-only pointer fields so events-3 (which has no concept of them, and treats every
 * `requirements` entry as an active gate) sees exactly one gate. Pure — returns a new object.
 *
 * Mirrors events-api `src/admin/events/apply-effective-requirements.ts` (the publish-time collapse). KEEP THE
 * TWO IN SYNC. Call this only when `project_gate_type !== undefined` (i.e. brandlive authored the gating);
 * a `undefined` pointer means a genuine legacy events-3 event and must be left untouched.
 */
export default function narrowRegistrationToActiveGate(settings: RegistrationWithGatePointer): Registration {
	// Defensive contract guard: a genuine legacy events-3 event has no brandlive pointer — there is nothing to
	// collapse, so leave it untouched. Callers already guard on this, but the utility must not wipe gating if misused.
	if (settings.project_gate_type === undefined) return { ...settings };

	const next: RegistrationWithGatePointer = { ...settings };
	const gateType = settings.project_gate_type;

	// events-3 owns the data after this — remove the platform-only pointer fields.
	delete next.project_gate_type;
	delete next.domain_gate_mode;

	// Explicit "open" (null) or a non-gated registration → strip all gating enforcement data.
	if (gateType === null || settings.type !== RegistrationTypes.gated) {
		next.requirements = {};
		next.blockedDomainEmails = null;
		next.useBlockedDomainEmails = false;
		return next;
	}

	// Password gating uses `password_gating_enabled`, not a `requirements` entry.
	if (gateType === GateTypes.password) {
		next.requirements = {};
		next.blockedDomainEmails = null;
		next.useBlockedDomainEmails = false;
		return next;
	}

	// Narrow `requirements` to only the active gate's entry.
	const entry = gateType !== undefined ? settings.requirements?.[gateType] : undefined;
	next.requirements = (entry !== undefined ? { [gateType as GateTypes]: entry } : {}) as RequirementMap;

	// The block list is only meaningful for the `domain_email` gate in 'block' sub-mode.
	const isDomainBlockMode = gateType === GateTypes.domain_email && settings.domain_gate_mode === 'block';
	if (isDomainBlockMode) {
		// Enforcing blocked-domains only — drop the allow-side requirement, keep the block list.
		next.requirements = {};
	} else {
		// Any other gate (allow mode, shared_passcode, passcode_list, …) — drop the block list.
		next.blockedDomainEmails = null;
		next.useBlockedDomainEmails = false;
	}

	return next;
}
